import {Button, Col, Form, InputGroup, Row, ToggleButton, ToggleButtonGroup} from "react-bootstrap";
import styles from "./Notes.module.css";
import {AsyncTypeahead} from "react-bootstrap-typeahead";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faX} from "@fortawesome/free-solid-svg-icons";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {findTagsLikeNameAsync} from "./noteSlice";
import {apiRequest, loginRequest} from "../../authConfig";
import {useMsal} from "@azure/msal-react";
import React, {useState} from "react";
import {FilterNotesInterface} from "./FilterNotesInterface";
import {findCustomersLikeNameAsync} from "../customers/customersSlice";
import moment from "moment";
import {QuickDateFilterInterface} from "./QuickDateFilterInterface";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {registerLocale, setDefaultLocale} from "react-datepicker";
import fi from 'date-fns/locale/fi';

registerLocale('fi', fi)

export const Filters = (props: FilterNotesInterface) => {
    const {instance, accounts} = useMsal();
    const tagLookupLoading = useAppSelector((state) => state.note.tagsLookupStatus == 'loading');
    const customerLookupLoading = useAppSelector((state) => state.customers.customerLookupStatus == 'loading');
    const dispatch = useAppDispatch();
    const tagLookup = useAppSelector((state) => state.note.tagLookup);
    const customerLookup = useAppSelector((state) => state.customers.customerLookup);

    const handleTagSearch = async (query: string) => {
        const apiToken = await requestApiAccessToken();
        await dispatch(findTagsLikeNameAsync({
            apiToken: apiToken,
            name: query
        }));
    };

    const handleCustomerSearch = async (query: string) => {
        const apiToken = await requestApiAccessToken();
        await dispatch(findCustomersLikeNameAsync({apiToken: apiToken, name: query}));
    };

    const requestApiAccessToken = async (): Promise<string> => {
        const request = {
            ...apiRequest,
            account: accounts[0],
        };

        try {
            const response = await instance.acquireTokenSilent(request);
            return response.accessToken;
        } catch (error) {
            const response = await instance.acquireTokenSilent(request);
            return response.accessToken;
        }
    };

    const shouldUpdateLookupSelection = (selection: any[]): boolean => {
        const target = selection[selection.length - 1];
        const selections = selection.filter(s => {
            return s?.name === target?.name;
        });

        return selections?.length <= 1;
    };

    const [tagSelections, setTagSelections] = useState<any[]>([]);
    const [customerSelections, setCustomerSelections] = useState<any[]>([]);
    const [subject, setSubject] = useState<string>('');
    const [content, setContent] = useState<string>('');
    const [showArchived, setShowArchived] = useState<boolean>(false);
    const [showWithoutCustomers, setShowWithoutCustomers] = useState<boolean>(false);
    const [quickDateFilterState, setQuickDateFilterState] = useState<QuickDateFilterInterface>({state: 'NOT_SET'});
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [useTimeIntervall, setUseTimeIntervall] = useState<boolean>(false);

    const filterNotes = async () => {

        const filterQueries: string[] = [];
        const orderByClause = '$orderby=created desc';

        if (tagSelections?.length > 0) {
            let tagsQuery = 'tags/any(s:';
            const allTags = tagSelections.map(selection => `contains(s/name, '${selection.name}')`);
            allTags.forEach((tag, index) => {
                tagsQuery = tagsQuery.concat((++index === allTags.length) ? tag : `${tag} or `);
            });

            tagsQuery = `${tagsQuery})`;
            filterQueries.push(tagsQuery);
        }

        if (customerSelections?.length > 0) {
            let customersQuery = 'customers/any(c: ';
            const allCustomers = customerSelections.map(selection => `c/id eq ${selection.id}`);
            allCustomers.forEach((customer, index) => {
                customersQuery = customersQuery.concat((++index === allCustomers.length) ? customer : `${customer} or `);
            })

            customersQuery = `${customersQuery})`;
            filterQueries.push(customersQuery);
        }

        if (subject.length > 0) {
            const subjectQuery = `contains(subject, '${subject}')`;
            filterQueries.push(subjectQuery);
        }

        if (content.length > 0) {
            const contentQuery = `contains(content, '${content}')`;
            filterQueries.push(contentQuery);
        }

        if (!showArchived) {
            const showArchivedQuery = `archived eq ${showArchived}`;
            filterQueries.push(showArchivedQuery);
        }

        if (showWithoutCustomers) {
            const showWithoutCustomersQuery = 'customers/$count eq 0';
            filterQueries.push(showWithoutCustomersQuery);
        }

        const dateFormat = 'YYYY-MM-DD';
        
        if (!useTimeIntervall && quickDateFilterState.state != 'NOT_SET') {
            const now = moment();

            switch (quickDateFilterState.state) {
                case "THIS_WEEK":
                    filterQueries.push(
                        `created ge ${now.startOf('isoWeek').format(dateFormat)}`);
                    break;
                case "LAST_TWO_WEEKS":
                    filterQueries.push(
                        `created ge ${now.startOf('isoWeek').subtract(1, 'week').format(dateFormat)}`);
                    break;
                case "THIS_MONTH":
                    filterQueries.push(
                        `created ge ${now.startOf('month').format(dateFormat)}`);
                    break;
                case "LAST_TWO_MONTH":
                    filterQueries.push(
                        `created ge ${now.startOf('month').subtract(1, 'month').format(dateFormat)}`);
                    break;
            }
        }
        
        if (useTimeIntervall) {
            const start = moment(startDate).format(dateFormat);
            const end = moment(endDate).format(dateFormat);
            filterQueries.push(`created ge ${start} and created le ${end}`);
        }

        if (filterQueries.length === 0) {
            await props.filterNotesHandler(`&${orderByClause}`);
            return;
        }

        const query = filterQueries.join(' and ').concat(`& ${orderByClause}`);
        await props.filterNotesHandler(`&$filter=${query}`);
    }

    const clearFilters = () => {
        setContent('');
        setSubject('');
        setTagSelections([]);
        setCustomerSelections([]);
        setShowArchived(false);
        setShowWithoutCustomers(false);
        setQuickDateFilterState({state: 'NOT_SET'});
        setStartDate(new Date());
        setEndDate(new Date());
        setUseTimeIntervall(false);
    }

    const onSubjectChange = (value: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSubject(value.target.value);
    }

    const onContentChange = (value: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setContent(value.target.value)
    }

    const onShowArchivedChange = (value: React.ChangeEvent<HTMLInputElement>) => {
        setShowArchived(value.target.checked)
    }

    const onWithoutCustomersChange = (value: React.ChangeEvent<HTMLInputElement>) => {
        setShowWithoutCustomers(value.target.checked)
    }

    const handleQuickDateFilterChange = (value: string) => {
        switch (value) {
            case 'NOT_SET':
                setQuickDateFilterState({state: 'NOT_SET'});
                break;
            case 'THIS_WEEK':
                setQuickDateFilterState({state: 'THIS_WEEK'});
                break;
            case 'LAST_TWO_WEEKS':
                setQuickDateFilterState({state: 'LAST_TWO_WEEKS'});
                break;
            case 'THIS_MONTH':
                setQuickDateFilterState({state: 'THIS_MONTH'});
                break;
            case 'LAST_TWO_MONTH':
                setQuickDateFilterState({state: 'LAST_TWO_MONTH'});
                break;
            default:
                setQuickDateFilterState({state: 'NOT_SET'});
        }
    }

    return (
        <Form>
            <Row>
                <Col className="col-12 col-lg-6 mb-2">
                    <Form.Group>
                        <Form.Label>Tag</Form.Label>
                        <InputGroup>
                            <AsyncTypeahead
                                newSelectionPrefix="Add new tag: "
                                filterBy={() => true}
                                id="searchTag"
                                labelKey="name"
                                isLoading={tagLookupLoading}
                                minLength={1}
                                onSearch={handleTagSearch}
                                options={tagLookup}
                                placeholder="Search for tags"
                                selected={tagSelections}
                                onChange={(v) => {
                                    if (shouldUpdateLookupSelection(v)) {
                                        setTagSelections(v);
                                    }
                                }}
                                multiple={true}
                                allowNew={false}
                            />
                            <Button variant="secondary"
                                    onClick={() => {
                                        setTagSelections([]);
                                    }}
                            >
                                <FontAwesomeIcon icon={faX}/>
                            </Button>
                        </InputGroup>
                    </Form.Group>
                </Col>
                <Col className="col-12 col-lg-6 mb-2">
                    <Form.Group>
                        <Form.Label>Customer</Form.Label>
                        <InputGroup>
                            <AsyncTypeahead
                                filterBy={() => true}
                                id="searchCustomer"
                                labelKey="name"
                                isLoading={customerLookupLoading}
                                minLength={1}
                                onSearch={handleCustomerSearch}
                                options={customerLookup}
                                placeholder="Search for customers"
                                selected={customerSelections}
                                onChange={(v) => {
                                    if (shouldUpdateLookupSelection(v)) {
                                        setCustomerSelections(v);
                                    }
                                }}
                                multiple={true}
                            />
                            <Button variant="secondary"
                                    onClick={() => {
                                        setCustomerSelections([]);
                                    }}
                            >
                                <FontAwesomeIcon icon={faX}/>
                            </Button>
                        </InputGroup>
                    </Form.Group>
                </Col>
                <Col className="col-12 col-lg-6 mb-2">
                    <Form.Group>
                        <Form.Label>Subject</Form.Label>
                        <InputGroup>
                            <Form.Control
                                id="filterBySubject"
                                name="filterBySubject"
                                type="text"
                                value={subject}
                                onChange={onSubjectChange}
                            />
                        </InputGroup>
                    </Form.Group>
                </Col>
                <Col className="col-12 col-lg-6 mb-2">
                    <Form.Group>
                        <Form.Label>Content</Form.Label>
                        <InputGroup>
                            <Form.Control
                                as="textarea"
                                id="filterByContent"
                                name="filterByContent"
                                value={content}
                                onChange={onContentChange}
                            />
                        </InputGroup>
                    </Form.Group>
                </Col>
                <Col className="col-12 col-lg-6 mb-2">
                    <Form.Check type="checkbox">
                        <Form.Check.Label>Include archived</Form.Check.Label>
                        <Form.Check.Input
                            id="archived"
                            checked={showArchived}
                            type="checkbox"
                            onChange={onShowArchivedChange}/>
                    </Form.Check>
                </Col>
                <Col className="col-12 col-lg-6 mb-2">
                    <Form.Check type="checkbox">
                        <Form.Check.Label>Without customers</Form.Check.Label>
                        <Form.Check.Input
                            id="withoutcustomers"
                            checked={showWithoutCustomers}
                            type="checkbox"
                            onChange={onWithoutCustomersChange}/>
                    </Form.Check>
                </Col>
                <Col className="col-12">
                    <hr/>
                </Col>
                <Col className="col-12 mb-2" hidden={useTimeIntervall}>
                    <ToggleButtonGroup
                        value={quickDateFilterState.state}
                        type="radio"
                        name="options"
                        onChange={handleQuickDateFilterChange}>
                        <ToggleButton id="quick-date-filter-not-set" value="NOT_SET">
                            All dates
                        </ToggleButton>
                        <ToggleButton id="quick-date-filter-this-week" value="THIS_WEEK">
                            This week
                        </ToggleButton>
                        <ToggleButton id="quick-date-filter-last-two-weeks" value="LAST_TWO_WEEKS">
                            Last two weeks
                        </ToggleButton>
                        <ToggleButton id="quick-date-filter-this-month" value="THIS_MONTH">
                            This month
                        </ToggleButton>
                        <ToggleButton id="quick-date-filter-last-month" value="LAST_TWO_MONTH">
                            Last two month
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Col>
                <Col className="col-12 mb-2">
                    <Form.Check
                        label="Use date intervall"
                        className="mb-2"
                        type="checkbox"
                        id="use-date-intervall"
                        checked={useTimeIntervall}
                        onChange={() => setUseTimeIntervall(!useTimeIntervall)}
                    >
                    </Form.Check>
                    <Col className="col-xl-2 col-lg-3 col-md-12 mb-2" hidden={!useTimeIntervall}>
                        <Form.Label>From</Form.Label>
                        <DatePicker
                            className="form-control"
                            label="foo"
                            locale={fi}
                            selectStart
                            dateFormat="yyyy-MM-dd"
                            selected={startDate}
                            startDate={startDate}
                            endDate={endDate}
                            onChange={(date: Date) => setStartDate(date)}/>
                    </Col>
                    <Col className="col-xl-2 col-lg-3 col-md-12 mb-2" hidden={!useTimeIntervall}>
                        <Form.Label>To</Form.Label>
                        <DatePicker
                            className="form-control"
                            locale={fi}
                            selectEnd
                            dateFormat="yyyy-MM-dd"
                            selected={endDate}
                            startDate={startDate}
                            endDate={endDate}
                            minDate={startDate}
                            onChange={(date: Date) => setEndDate(date)}/>
                    </Col>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Col>
                        <Button
                            variant="secondary"
                            onClick={async () => clearFilters()}>
                            Clear filters
                        </Button>
                    </Col>
                </Col>
                <Col>
                    <Col className={styles.right}>
                        <Button
                            variant="primary"
                            onClick={async () => await filterNotes()}>
                            Search
                        </Button>
                    </Col>
                </Col>
            </Row>
        </Form>
    );
}
